import React from 'react';
import { Box, Container, Typography, Grid, Button, List, ListItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/footer';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import ApplyFlowButton from './ApplyFlowButton';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/458x375?text=Image+Coming+Soon';

export default function LoanProgramTemplate({
  title,
  subtitle = 'Our Loan Programs',
  heroImage,
  description,
  descriptionBold,
  whyBuyersConsider,
  contentImage = PLACEHOLDER_IMAGE,
  eligibilityTitle = 'Basic Eligibility',
  eligibilityItems = [],
  benefitsTitle = 'Benefits',
  benefitsItems = [],
}) {
  const navigate = useNavigate();

  return (
    <>
      <LandingPageNav />
      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />

      {/* ZONE 1: HERO BANNER */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflowX: 'hidden',
          backgroundImage: heroImage
            ? `linear-gradient(rgba(10,10,10,.35), rgba(10,10,10,.45)), url(${heroImage})`
            : undefined,
          backgroundColor: heroImage ? undefined : '#13334D',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: { xs: '22vh', md: '28vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: { xs: 2.25, md: 3 },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, px: { xs: 1.5, md: 3 }, maxWidth: 675 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#fff',
              fontWeight: 800,
              lineHeight: 1.1,
              fontSize: { xs: '1.7rem', md: '2.45rem' },
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="h5"
              sx={{
                color: '#fff',
                fontWeight: 400,
                fontStyle: 'italic',
                fontSize: { xs: '0.825rem', md: '1.05rem' },
                mt: 0.75,
                mb: 1.875,
                textShadow: '0 2px 6px rgba(0,0,0,0.5)',
              }}
            >
              {subtitle}
            </Typography>
          )}
          <ApplyFlowButton label="APPLY TODAY" />
        </Box>
      </Box>

      {/* Back link */}
      <Container maxWidth="lg" sx={{ pt: 2.25 }}>
        <Button variant="text" onClick={() => navigate('/loan-programs')}>
          ← Back to Loan Programs
        </Button>
      </Container>

      {/* ZONE 2: TWO-COLUMN BODY */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4.5 } }}>
        <Grid container spacing={{ xs: 3, md: 3.75 }} alignItems="flex-start">
          {/* LEFT COLUMN — text */}
          <Grid item xs={12} md={6}>
            {(description || descriptionBold) && (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  lineHeight: 2,
                  mb: 3.75,
                }}
              >
                {descriptionBold && (
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    {descriptionBold}
                  </Box>
                )}
                {descriptionBold && description ? ' ' : ''}
                {description}
              </Typography>
            )}
            {whyBuyersConsider && (
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ mb: 1.5, fontSize: { xs: '1.05rem', md: '1.3rem' } }}
                >
                  Why Buyers Consider This:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', md: '1.05rem' },
                    lineHeight: 2,
                  }}
                >
                  {whyBuyersConsider}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* RIGHT COLUMN — image only */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={contentImage}
                alt={`${title} illustration`}
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  aspectRatio: '458 / 375',
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: 2,
                  display: 'block',
                  mx: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* CENTERED CTA — between body and eligibility/benefits, full-width centered */}
      <Container maxWidth="lg" sx={{ pb: { xs: 3.75, md: 5.25 }, textAlign: 'center' }}>
        <Typography
          variant="h5"
          fontWeight={700}
          align="center"
          sx={{ mb: 1.875, lineHeight: 1.3, fontSize: { xs: '1.125rem', md: '1.4rem' } }}
        >
          To get started, find a loan officer near you today!
        </Typography>
        <ApplyFlowButton label="GET STARTED" />
      </Container>

      {/* ZONE 3: TWO-COLUMN ELIGIBILITY / BENEFITS */}
      <Container maxWidth="lg" sx={{ pb: { xs: 4.5, md: 7.5 } }}>
        <Grid container spacing={2.25}>
          {/* LEFT — Basic Eligibility (light gray, matches Benefits) */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#E8E8E8',
                color: 'text.primary',
                p: { xs: 2.25, md: 3 },
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ mb: 1.5, fontSize: { xs: '1.125rem', md: '1.3rem' } }}
              >
                {eligibilityTitle}
              </Typography>
              <List sx={{ listStyleType: 'disc', pl: 2.25 }}>
                {eligibilityItems.map((item, i) => (
                  <ListItem key={i} sx={{ display: 'list-item', py: 0.375, pl: 0 }}>
                    <Typography
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '0.95rem' },
                      }}
                    >
                      {item}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* RIGHT — Benefits (light gray) */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#E8E8E8',
                color: 'text.primary',
                p: { xs: 2.25, md: 3 },
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ mb: 1.5, fontSize: { xs: '1.125rem', md: '1.3rem' } }}
              >
                {benefitsTitle}
              </Typography>
              <List sx={{ listStyleType: 'disc', pl: 2.25 }}>
                {benefitsItems.map((item, i) => (
                  <ListItem key={i} sx={{ display: 'list-item', py: 0.375, pl: 0 }}>
                    <Typography
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '0.95rem' },
                      }}
                    >
                      {item}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* MOBILE-ONLY BOTTOM CTA — duplicate of the right-column CTA from Zone 2.
          Hidden on md+ where the in-content CTA is already visible. */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          textAlign: 'center',
          px: 2.25,
          pb: 4.5,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ mb: 1.875, lineHeight: 1.4, fontSize: '1.05rem' }}
        >
          Find Your Loan Officer Today
        </Typography>
        <ApplyFlowButton label="GET STARTED" />
      </Box>

      <Footer />
    </>
  );
}
